/**
 * Confirms a password reset.
 *
 * Mounted when ``window.location.pathname === "/auth/reset"`` so the
 * link in the reset email lands here with ``?token=...``. Two
 * password fields (with a client-side match check) → POST to the
 * backend → on success, navigate to ``/`` so the user logs in fresh.
 *
 * On a 400 (token expired/invalid) we render an error with a
 * "Request a new link" action that pivots to
 * :class:`PasswordResetRequestScreen`.
 */

import { useState } from "react";
import type { FormEvent } from "react";

import { authApi } from "../api/auth";
import { ApiError } from "../api/client";
import { useT } from "../lib/i18n";
import { useApi } from "./AuthProvider";
import { AuthShell } from "./AuthShell";

interface PasswordResetConfirmScreenProps {
  /** Override for tests; production reads from ``window.location``. */
  search?: string;
  /** Override for tests; production calls ``window.location.replace``. */
  navigate?: (url: string) => void;
  /** Called when the user opts to request a new link after a failure. */
  onRequestNewLink?: () => void;
}

const HTTP_BAD_REQUEST = 400;

export function PasswordResetConfirmScreen({
  search,
  navigate,
  onRequestNewLink,
}: PasswordResetConfirmScreenProps = {}): JSX.Element {
  const [, T] = useT();
  const client = useApi();
  const params = new URLSearchParams(search ?? window.location.search);
  const token = params.get("token");
  const goTo = navigate ?? ((url: string) => window.location.replace(url));

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenInvalid, setTokenInvalid] = useState(token === null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (token === null) {
      setTokenInvalid(true);
      return;
    }
    if (password !== confirm) {
      setError(T("passwordsDoNotMatch"));
      return;
    }
    setSubmitting(true);
    try {
      await authApi(client).confirmPasswordReset(token, password);
      goTo("/");
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === HTTP_BAD_REQUEST) {
          setTokenInvalid(true);
        } else {
          setError(caught.message);
        }
      } else {
        setError(T("somethingWentWrong"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (tokenInvalid) {
    return (
      <AuthShell title={T("resetPassword")} ariaLabel={T("resetPassword")}>
        <div className="vz-error" role="alert">
          {T("resetLinkInvalid")}
        </div>
        {onRequestNewLink !== undefined && (
          <button
            type="button"
            className="vz-btn vz-btn-primary"
            onClick={onRequestNewLink}
            style={{ justifyContent: "center", padding: "10px" }}
          >
            {T("requestNewResetLink")}
          </button>
        )}
        <a
          href="/"
          className="vz-btn vz-btn-ghost"
          style={{
            justifyContent: "center",
            padding: "10px",
            textDecoration: "none",
          }}
        >
          {T("backToLogin")}
        </a>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={T("resetPassword")} ariaLabel={T("resetPassword")}>
      <form
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div className="vz-auth-field">
          <label htmlFor="reset-confirm-pw">{T("newPassword")}</label>
          <input
            id="reset-confirm-pw"
            className="vz-input"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="vz-auth-field">
          <label htmlFor="reset-confirm-pw2">{T("confirmPassword")}</label>
          <input
            id="reset-confirm-pw2"
            className="vz-input"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
        </div>
        {error !== null && (
          <div className="vz-error" role="alert">
            {error}
          </div>
        )}
        <button
          type="submit"
          className="vz-btn vz-btn-primary"
          disabled={submitting}
          style={{ justifyContent: "center", padding: "10px" }}
        >
          {submitting ? T("loading") : T("resetPassword")}
        </button>
      </form>
    </AuthShell>
  );
}
